import {
  type KeyboardEvent,
  type PointerEvent,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from "react";

import { useReducedMotion } from "motion/react";

import { Box } from "@hightouch/htui";

import { navMotion } from "src/components/layout/nav/nav-motion";
import {
  clampNavWidth,
  NAV_COLLAPSE_THRESHOLD,
  NAV_MAX_WIDTH,
  NAV_MIN_WIDTH,
} from "src/contexts/nav-width-context";
import * as analytics from "src/lib/analytics";

/** How far a press moves before it is a drag rather than a click. */
const DRAG_THRESHOLD_PX = 3;

/** Where each key takes the width: one step of the 8px grid per arrow, or either end. */
const KEY_WIDTHS = new Map<string, (width: number) => number>([
  ["ArrowLeft", (width) => width - 8],
  ["ArrowRight", (width) => width + 8],
  ["Home", () => NAV_MIN_WIDTH],
  ["End", () => NAV_MAX_WIDTH],
]);

type Props = {
  /** The docked sidebar, which follows the pointer without re-rendering the nav inside it. */
  frameRef: RefObject<HTMLElement>;
  contentRef: RefObject<HTMLElement>;
  /** The page's existing inset when collapsed, resolved from its spacing token. */
  collapsedWidth: number;
  /** The saved docked width. */
  width: number;
  onResize: (width: number) => void;
  /** A click or Enter on the edge, or a drag past the collapse threshold. */
  onCollapse: (source: "edge" | "resize", isKeyboard: boolean) => void;
};

/**
 * The docked sidebar's right edge: a drag or the arrow keys resize it, and a click, Enter, or a drag most of the way shut collapses it.
 * The grip follows hover vertically, then holds its height until the drag ends.
 */
export const SidebarResizeHandle = ({
  frameRef,
  contentRef,
  collapsedWidth,
  width,
  onResize,
  onCollapse,
}: Props) => {
  const gripRef = useRef<HTMLDivElement>(null);
  const isReducedMotion = useReducedMotion();
  const slides = useRef<Animation[]>([]);
  const gesture = useRef(0);
  const drag = useRef<{
    startX: number;
    startWidth: number;
    width: number;
    handle: HTMLDivElement;
    pointerId: number;
    isDragging: boolean;
  } | null>(null);
  const [liveWidth, setLiveWidth] = useState<number | null>(null);
  const widthOnFocus = useRef(width);
  const isResizing = liveWidth !== null;

  const positionGrip = (event: PointerEvent<HTMLDivElement>) => {
    const grip = gripRef.current;
    if (!grip || drag.current) return;
    const { top, height } = event.currentTarget.getBoundingClientRect();
    const halfGripHeight = grip.offsetHeight / 2;
    const y = Math.max(
      halfGripHeight,
      Math.min(event.clientY - top, height - halfGripHeight),
    );
    // Only the grip moves; the sidebar does not re-render as the pointer follows its edge.
    grip.style.transform = `translateY(calc(${y - height / 2}px - 50%))`;
  };

  // The frame and the page-wide nav width follow the pointer without a render; `null` hands both back to the saved width.
  const setFrameWidth = (next: number | null) => {
    frameRef.current?.style.setProperty(
      "width",
      next === null ? null : `${next}px`,
    );
    document.documentElement.style.setProperty(
      "--ht-nav-width",
      `${next ?? width}px`,
    );
  };

  const previewWidth = (next: number) => {
    const frame = frameRef.current;
    const content = contentRef.current;
    if (!frame || !content) return;
    const isClosing = next === 0;
    const fromWidth = frame.getBoundingClientRect().width;
    const fromTransform = getComputedStyle(content).transform;
    const contentWidth = isClosing
      ? content.getBoundingClientRect().width
      : next;
    const shouldAnimate =
      !isReducedMotion &&
      (isClosing || drag.current?.width === 0 || slides.current.length > 0);
    for (const slide of slides.current) slide.cancel();
    slides.current = [];

    setFrameWidth(next);
    // The page keeps its collapsed inset while the nav contents slide intact, rather than squeezing every row.
    if (isClosing) frame.style.width = `${collapsedWidth}px`;
    content.style.width = `${contentWidth}px`;
    content.style.transform = isClosing
      ? `translateX(-${contentWidth}px)`
      : "translateX(0)";
    if (!shouldAnimate) {
      if (!isClosing) {
        content.style.removeProperty("width");
        content.style.removeProperty("transform");
      }
      return;
    }

    const options = {
      duration:
        (isClosing ? navMotion.foldDuration : navMotion.unfoldDuration) * 1000,
      easing: `cubic-bezier(${navMotion.foldEase.join(", ")})`,
      fill: "both" as const,
    };
    const moves = [
      frame.animate(
        [
          { width: `${fromWidth}px` },
          { width: `${isClosing ? collapsedWidth : next}px` },
        ],
        options,
      ),
      content.animate(
        [{ transform: fromTransform }, { transform: content.style.transform }],
        options,
      ),
    ];
    slides.current = moves;
    void Promise.all(moves.map((slide) => slide.finished)).then(
      () => {
        if (slides.current !== moves) return;
        for (const slide of moves) slide.cancel();
        slides.current = [];
        if (!isClosing) {
          content.style.removeProperty("width");
          content.style.removeProperty("transform");
        }
      },
      () => {},
    );
  };

  useEffect(() => {
    const content = contentRef.current;
    return () => {
      gesture.current += 1;
      for (const slide of slides.current) slide.cancel();
      slides.current = [];
      content?.style.removeProperty("width");
      content?.style.removeProperty("transform");
    };
  }, [contentRef]);

  /** Ends the drag, and returns the width it reached. */
  const endDrag = () => {
    const current = drag.current;
    drag.current = null;
    setLiveWidth(null);
    if (current?.handle.hasPointerCapture(current.pointerId)) {
      current.handle.releasePointerCapture(current.pointerId);
    }
    return current?.width ?? null;
  };

  const cancelDrag = () => {
    if (!drag.current) return;
    previewWidth(width);
    endDrag();
    setFrameWidth(null);
  };

  // Escape abandons a drag, as an interrupted touch does, leaving the saved width.
  const latestCancel = useRef(cancelDrag);
  latestCancel.current = cancelDrag;
  useEffect(() => {
    if (!isResizing) return;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      latestCancel.current();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isResizing]);

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    gesture.current += 1;
    positionGrip(event);
    // No text selection follows the drag across the page.
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    const startWidth = frameRef.current?.getBoundingClientRect().width || width;
    drag.current = {
      startX: event.clientX,
      startWidth,
      width: startWidth,
      handle: event.currentTarget,
      pointerId: event.pointerId,
      isDragging: false,
    };
    // The grip stays visible off the edge during a drag, which Escape can still cancel.
    setLiveWidth(startWidth);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    positionGrip(event);
    const current = drag.current;
    if (!current) return;
    const distance = event.clientX - current.startX;
    if (!current.isDragging) {
      // A press that wanders a few px is still a click, which collapses the sidebar.
      if (Math.abs(distance) < DRAG_THRESHOLD_PX) return;
      current.isDragging = true;
    }
    const nextWidth = current.startWidth + distance;
    // Keep the captured handle mounted while closed, so the same drag can open the sidebar again.
    const next =
      nextWidth < NAV_COLLAPSE_THRESHOLD ? 0 : clampNavWidth(nextWidth);
    if (next === current.width) return;
    previewWidth(next);
    current.width = next;
    setLiveWidth(next);
  };

  const handlePointerUp = () => {
    if (!drag.current) return;
    if (!drag.current.isDragging) {
      endDrag();
      onCollapse("edge", false);
      return;
    }
    const next = endDrag();
    if (next === 0) {
      const moves = slides.current;
      const releasedGesture = gesture.current;
      void Promise.all(moves.map((slide) => slide.finished)).then(
        () => {
          if (!drag.current && gesture.current === releasedGesture) {
            onCollapse("resize", false);
          }
        },
        () => {},
      );
      return;
    }
    setFrameWidth(null);
    if (next !== null && next !== width) {
      onResize(next);
      analytics.track("nav_resized", { width: next, input: "pointer" });
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      onCollapse("edge", true);
      return;
    }
    const toWidth = KEY_WIDTHS.get(event.key);
    if (!toWidth) return;
    event.preventDefault();
    onResize(clampNavWidth(toWidth(width)));
  };

  return (
    <Box
      data-group
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize sidebar"
      aria-valuenow={liveWidth ?? width}
      aria-valuemin={0}
      aria-valuemax={NAV_MAX_WIDTH}
      tabIndex={0}
      position="absolute"
      top={0}
      bottom={0}
      // The visible grip is narrow, but the full edge gives the pointer a 16px target.
      right={-2}
      width={4}
      // Above the nav's own surface, so it cannot cover the grip or half its hit area.
      zIndex={3}
      display="flex"
      justifyContent="center"
      cursor="col-resize"
      // A touch drag resizes instead of scrolling the page; Chakra has no `touch-action` prop.
      sx={{ touchAction: "none" }}
      _focusVisible={{
        outline: "none",
      }}
      onPointerEnter={positionGrip}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={cancelDrag}
      onLostPointerCapture={cancelDrag}
      onKeyDown={handleKeyDown}
      onFocus={() => {
        widthOnFocus.current = width;
        if (!drag.current) gripRef.current?.style.removeProperty("transform");
      }}
      // Arrow keys save every step, but count as one resize.
      onBlur={() => {
        if (width !== widthOnFocus.current) {
          analytics.track("nav_resized", { width, input: "keyboard" });
        }
      }}
    >
      <Box
        ref={gripRef}
        aria-hidden
        position="absolute"
        top="50%"
        transform="translateY(-50%)"
        width="calc(var(--chakra-sizes-1) - var(--chakra-sizes-px))"
        height={10}
        borderRadius="full"
        bg="base.borderHover"
        opacity={isResizing ? 1 : 0}
        pointerEvents="none"
        transitionProperty="opacity, background-color"
        transitionDuration="fast"
        transitionTimingFunction="easeOutQuad"
        _groupHover={{ opacity: 1 }}
        _groupFocusVisible={{ opacity: 1, bg: "primary.base" }}
      />
    </Box>
  );
};
